/**
 * game-editors/index.js -- registry + detection for optional, per-ROM
 * "party/save editor" plugins (e.g. web/src/game-editors/ff6.js).
 *
 * This is a deliberately separate, bolt-on layer: nothing in main.js,
 * core.js, input.js, or storage.js needs to know a specific plugin exists.
 * main.js only ever calls detectEditor(core) once per loaded ROM and, if it
 * gets a match back, renders whatever that plugin's `fields` + `read()`
 * describe -- see wireGameEditor() there. Deleting this whole directory (and
 * that one wiring block) fully removes the feature with no trace elsewhere,
 * which is exactly what was asked for: a plugin system that can be pulled
 * out later without touching the mainline emulator/storage/sync code.
 *
 * A plugin reads and writes the LIVE running game's memory via
 * core.wram() -- not an offline .snes9xstate file. A save state is an
 * opaque, core-internal serialize() blob (see CLAUDE.md), not a flat memory
 * dump, so there is no reliable way to locate "Terra's HP" inside one
 * without loading it into the core first anyway. Loading a state with
 * core.unserialize() and then editing wram() live achieves the same result
 * (edit, then make a fresh save state) far more robustly, since it reuses
 * the exact same address space every RAM-watch/cheat tool for this game
 * already documents -- not a snes9x-build-specific struct layout that could
 * silently shift on the next core rebuild.
 */

import { ff6Editor } from './ff6.js';

const PLUGINS = [ff6Editor];

/**
 * Parse just enough of the SNES cartridge header to identify a game.
 * @param {Uint8Array} romBytes
 */
export function readSnesHeader(romBytes) {
  function tryAt(offset) {
    if (romBytes.length < offset + 32) return null;
    const title = new TextDecoder('latin1')
      .decode(romBytes.subarray(offset, offset + 21))
      .replace(/\0/g, '')
      .trimEnd();
    const mapMode = romBytes[offset + 21];
    const checksum = romBytes[offset + 28] | (romBytes[offset + 29] << 8);
    const complement = romBytes[offset + 30] | (romBytes[offset + 31] << 8);
    const valid = (checksum ^ complement) === 0xffff;
    return { title, mapMode, checksum, complement, valid };
  }

  // HiROM's header lives at $FFC0, LoROM's at $7FC0. Try both and trust
  // whichever one's checksum/complement pair is internally consistent (they
  // must XOR to 0xFFFF on a real cartridge) -- the standard trick emulators
  // use to auto-detect cartridge mapping without already knowing it.
  const hirom = tryAt(0xffc0);
  const lorom = tryAt(0x7fc0);
  if (hirom?.valid && !lorom?.valid) return { ...hirom, mapping: 'hirom' };
  if (lorom?.valid && !hirom?.valid) return { ...lorom, mapping: 'lorom' };
  if (hirom) return { ...hirom, mapping: 'hirom' };
  if (lorom) return { ...lorom, mapping: 'lorom' };
  return null;
}

/**
 * @param {import('../core.js').Snes9xCore} core
 * @returns {object|null} the matching plugin (see ff6.js for the shape), or
 *   null if the currently loaded ROM has none
 */
export function detectEditor(core) {
  const rom = core.rom();
  if (rom.length === 0) return null;
  const header = readSnesHeader(rom);
  if (!header) return null;
  return PLUGINS.find((plugin) => plugin.matches(header)) ?? null;
}
